import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import {
  AnalysesTypes,
  AnalysisResponse,
  HistoryResult,
  AboutAnalysis,
  AnalysisResult,
  AnalysisConfiguration,
  AnalysesTypesLowercase,
  NewResultModel,
  Type,
  MeasurementsResponse,
  Question,
  NewHabitsResult,
  HealthscoreAbout,
  AnalysesResultDetails,
  BloodCatalogue,
  CatalogueAnalysisAbout,
  BloodPanel,
  HSScore,
  AnalysisPopulation,
  DnaReportAnalysis,
  MeasurementConfiguration,
  NewMeasurementsResult,
  HabitsConfiguration,
  QuestionnaireCreate,
} from 'src/app/analyses/interfaces/analyses.types';
import {
  HealthScoreFactorResultResponse,
  HealthScoreHistoryResponse,
  HealthScoreStatusResponse,
  HealthScoreComparisonResponse,
  HealthScoreParticipantsResponse,
} from '../../healthscore/interfaces/healthscore-api.interface';
import {
  SemanticType,
  SemanticTypeValue,
} from '../../shared/constants/semantic-type';
import { resolveTypePath } from '../util/link-util';
import {
  Recommendation,
  RecommendationDNAResponse,
} from '../../shared/interfaces/recommendation.interface';
import { BloodConfiguration } from 'src/app/blood/interfaces/blood-configuration.interface';
import { AnalysisId } from '../interfaces/analysis-id.enum';

@Injectable({
  providedIn: 'root',
})
export class AnalysesService {
  analyses: {
    [id: string]: {
      name: string;
      id: string;
      metrics?: [];
      type?: string;
      results?: { [id: string]: any };
    };
  } = {};
  questionnaire: {
    questions: Question[];
    inventoryItemId: string;
  } = this.getEmptyQuestionnaire();

  private readonly headers = {
    xPagingContinuation: 'X-Paging-Continuation',
    xPagingTop: 'X-Paging-Top',
  };

  constructor(private http: HttpClient) {}

  about(
    analysisId: string,
    type: AnalysesTypesLowercase
  ): Observable<AboutAnalysis> {
    const typePath = resolveTypePath(type);
    return this.http.get<AboutAnalysis>(
      `/api/${typePath}/analyses/${analysisId}/about`
    );
  }

  all(
    type: AnalysesTypesLowercase,
    search: string = null,
    filter: string = null
  ): Observable<any[]> {
    let queryParams = new HttpParams();

    if (search) {
      queryParams = queryParams.append('searchQuery', search);
    }

    if (filter) {
      queryParams = queryParams.append('levelTypes', filter);
    }

    const typePath = resolveTypePath(type);
    return this.http.get<AnalysisResponse[]>(`/api/${typePath}/analyses`, {
      params: queryParams,
    });
  }

  getDnaReportAnalyses(id: string): Observable<DnaReportAnalysis[]> {
    return this.http.get<DnaReportAnalysis[]>(`/api/dna/analyses/${id}`);
  }

  getMeasurement(
    id: string,
    unitSystem: string = null,
    sex: string = null,
    ethnicity: string = null
  ) {
    let queryParams = new HttpParams();
    if (unitSystem) {
      queryParams = queryParams.append('unitSystem', unitSystem);
    }
    if (sex) {
      queryParams = queryParams.append('sex', sex);
    }
    if (ethnicity) {
      queryParams = queryParams.append('ethnicity', ethnicity);
    }
    return this.http.get<MeasurementsResponse>(
      `/api/measurement/configurations/onboarding/${id}`,
      {
        params: queryParams,
      }
    );
  }

  getHSScore(): Observable<HSScore> {
    return this.http.get<HSScore>(`/api/healthscore/score`).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  getHSAbout(): Observable<HealthscoreAbout> {
    return this.http.get<HealthscoreAbout>(`/api/healthscore/about`).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  getHSStatus(): Observable<HealthScoreStatusResponse> {
    return this.http.get<HealthScoreStatusResponse>(`/api/healthscore/status`);
  }

  getHSPopulation(): Observable<HealthScoreComparisonResponse> {
    return this.http.get<HealthScoreComparisonResponse>(
      `/api/healthscore/comparison`
    );
  }
  getHSHistory(days: number): Observable<HealthScoreHistoryResponse> {
    return this.http.get<HealthScoreHistoryResponse>(
      `/api/healthscore/history?days=${days}`
    );
  }
  getHSFactors(): Observable<HealthScoreFactorResultResponse[]> {
    return this.http.get<HealthScoreFactorResultResponse[]>(
      `/api/healthscore/factors`
    );
  }

  getResultDetails(
    id: string,
    resultId: string,
    type: AnalysesTypesLowercase
  ): Observable<AnalysesResultDetails> {
    return this.http.get<any>(
      `/api/${resolveTypePath(type)}/analyses/${id}/results/${resultId}/details`
    );
  }

  participants(): Observable<HealthScoreParticipantsResponse[]> {
    return this.http.get<HealthScoreParticipantsResponse[]>(
      `/api/healthscore/participants`
    );
  }

  getTypes(): Observable<Type[]> {
    return this.http
      .get<Type[]>(`${environment.api}/analyses/types`)
      .pipe(shareReplay());
  }

  listRecommendations(
    analysisId: string,
    resultId: string,
    type: AnalysesTypesLowercase
  ): Observable<RecommendationDNAResponse | Recommendation[]> {
    if (type == AnalysesTypesLowercase.DNA) {
      return this.http.get<RecommendationDNAResponse>(
        `/api/dna/analyses/${analysisId}/recommendations`
      );
    }

    return this.http
      .get<Recommendation[]>(
        `/api/${resolveTypePath(
          type
        )}/analyses/${analysisId}/results/${resultId}/recommendations`
      )
      .pipe(map((items) => items.filter((item) => item.title && item.image)));
  }

  getDnaAnalysis(analysisId: string): Observable<AnalysisResult | any> {
    return this.http.get<AnalysisResult>(
      `/api/dna/analyses/${analysisId}/result`
    );
  }

  getPopulation(
    type: AnalysesTypesLowercase,
    analysisId: string
  ): Observable<AnalysisPopulation> {
    return this.http.get<AnalysisPopulation>(
      `/api/${type}/statistics/population/${analysisId}`
    );
  }

  getCalculationId(
    analysisId: string,
    requestId: string,
    type: AnalysesTypesLowercase
  ): Observable<AnalysisResult> {
    const params = new HttpParams().append('requestId', requestId);
    return this.http.get<AnalysisResult>(
      `/api/${resolveTypePath(type)}/analyses/${analysisId}/result`,
      { params }
    );
  }

  getAnalysis(
    id: string,
    resultId: string,
    publicCall = false,
    type: AnalysesTypesLowercase
  ): Observable<AnalysisResult | any> {
    // TODO - refactor this to a meaningful method. nobody knows why public call indicates questionnaire call...s
    if (publicCall) {
      return this.http.get<any[]>(
        `${environment.publicApi}/questionnaire/analyses/${id}`
      );
    }
    const typePath = resolveTypePath(type);
    return this.http
      .get<AnalysisResult>(
        `/api/${typePath}/analyses/${id}/results/${resultId}/result`
      )
      .pipe(
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
  }

  relatedAnalyses(
    id: string,
    type: AnalysesTypesLowercase
  ): Observable<AnalysisResponse[]> {
    return this.http.get<AnalysisResponse[]>(
      `/api/${resolveTypePath(type)}/analyses/${id}/related-analyses`
    );
  }

  configurations(
    id: string,
    publicCall = false,
    type: AnalysesTypesLowercase,
    measurementAnalysisType?: 'Measurement' | 'Correlation'
  ): Observable<AnalysisConfiguration | any[]> {
    // TODO - remove any
    if (publicCall) {
      return this.http.get<any[]>(
        `${environment.publicApi}/habits/configurations/${id}`
      );
    }

    if (
      type == AnalysesTypesLowercase.Measurements &&
      measurementAnalysisType
    ) {
      return this.http.get<AnalysisConfiguration>(
        `/api/${resolveTypePath(
          type
        )}/configurations/${id}?analysisType=${measurementAnalysisType}`
      );
    } else {
      return this.http.get<AnalysisConfiguration>(
        `/api/${resolveTypePath(type)}/configurations/${id}`
      );
    }
  }

  public bloodConfigurations(): Observable<BloodConfiguration[]> {
    return this.http.get<BloodConfiguration[]>(`/api/blood/configurations`);
  }

  public bloodConfiguration(id: string): Observable<BloodConfiguration> {
    return this.http.get<BloodConfiguration>(`/api/blood/configurations/${id}`);
  }
  
  public habitsConfiguration(id: string): Observable<HabitsConfiguration> {
    return this.http.get<HabitsConfiguration>(`/api/habits/configurations/${id}`);
  }

  public measurementConfiguration(
    id: string
  ): Observable<MeasurementConfiguration> {
    const type = id == AnalysisId.BloodPressure ? 'Correlation' : 'Measurement';

    return this.http.get<MeasurementConfiguration>(
      `/api/measurement/configurations/${id}`,
      { params: { analysisType: type } }
    );
  }

  public measurementCreate(
    data: NewMeasurementsResult
  ): Observable<{ requestId: string }> {
    return this.http.post<{ requestId: string }>(
      `/api/measurement/results`,
      data
    );
  }

  public measurementUpdate(
    calculationId: string,
    data: NewMeasurementsResult
  ): Observable<{ requestId: string }> {
    return this.http.put<{ requestId: string }>(
      `/api/measurement/results/${calculationId}`,
      data
    );
  }

  saveQuestionnaire(data: QuestionnaireCreate): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`/api/habits/results`, data);
  }

  get results(): Results {
    return {
      list: (
        id: string,
        type: AnalysesTypesLowercase,
        days?: number,
        token?: string,
        count = 20,
        page?: number
      ) => {
        let params: { [key: string]: any } = {};
        const headers = {};
        if (days) {
          params.days = String(days);
          params.count = -1; // To return all available
        } else if (token) {
          headers[this.headers.xPagingContinuation] = String(token);
          params.count = count;
        } else if (count >= 0 && Number(page) >= 0) {
          params = {};
          params.pageSize = count;
          params.page = page;
        } else if (count) {
          params.count = count;
        }

        return this.http
          .get<HistoryResult[]>(
            `/api/${resolveTypePath(type)}/analyses/${id}/history`,
            {
              headers,
              observe: 'response',
              params,
            }
          )
          .pipe(
            map((res: HttpResponse<HistoryResult[]>) => {
              return {
                results: res.body,
                token: res.headers.get(this.headers.xPagingContinuation),
              };
            })
          );
      },
      get: (id: string, resultId: string, type: AnalysesTypesLowercase) => {
        return this.http.get<any>(
          `/api/${resolveTypePath(
            type
          )}/analyses/${id}/results/${resultId}/result`
        );
      },
      save: (
        id: string,
        result: NewResultModel,
        type: AnalysesTypesLowercase,
        editMode: boolean,
        singleBlood = false
      ) => {
        if (editMode) {
          return this.http.put<{ requestId: string }>(
            `/api/${resolveTypePath(type)}/results/${id}`,
            result
          );
        } else {
          if (type === AnalysesTypesLowercase.Blood && !singleBlood) {
            return this.http.post<{ requestId: string }>(
              `/api/blood/reports`,
              result
            );
          }
          return this.http.post<{ requestId: string }>(
            `/api/${resolveTypePath(type)}/results`,
            result
          );
        }
      },
      delete: (resultId: string, type: AnalysesTypesLowercase) => {
        return this.http.delete<any>(
          `/api/${resolveTypePath(type)}/results/${resultId}`
        );
      },
    };
  }

  allGroups(): Observable<BloodPanel[]> {
    return this.http.get<BloodPanel[]>('/api/blood/panels-all');
  }

  get groups(): Groups {
    return {
      list: () => this.http.get<BloodPanelGroup[]>('/api/blood/panels'),
      get: (id: string) =>
        this.http.get<BloodPanelGroupAnalyses>(`/api/blood/panels/${id}`),
    };
  }

  get catalogue() {
    return {
      list: () => this.http.get<BloodCatalogue[]>('/api/blood/analyses/about'),
      get: (id: string) =>
        this.http.get<CatalogueAnalysisAbout>(
          `/api/blood/analyses/about/${id}`
        ),
    };
  }

  sortDnaBySemanticType(analyses: DnaReportAnalysis[]) {
    return analyses.sort((a: DnaReportAnalysis, b: DnaReportAnalysis) => {
      // sort first by semantic type, and second by analysis name
      if (a?.semanticType === b?.semanticType) {
        return a?.longName > b?.longName ? 1 : -1;
      } else if (a?.semanticType === SemanticType.NEUTRAL) {
        return 1;
      } else if (b?.semanticType === SemanticType.NEUTRAL) {
        return -1;
      } else if (
        SemanticTypeValue[a?.semanticType] > SemanticTypeValue[b?.semanticType]
      ) {
        return 1;
      }

      return -1;
    });
  }

  hideAnalysesWithoutResults(analyses: any[]) {
    return analyses.filter((analysis) => analysis.result);
  }

  get local(): Local {
    return {
      getQuestionnaire: () => {
        return this.questionnaire;
      },
      clearQuestionnaire: () => {
        this.questionnaire = this.getEmptyQuestionnaire();
      },

      saveQuestionnaire: (questionnaire: NewHabitsResult) => {
        questionnaire.questions.forEach((question) => {
          const index = this.questionnaire.questions.findIndex(
            ({ id }) => id === question.id
          );
          if (index > -1) {
            this.questionnaire.questions[index].answers = question.answers;
          } else {
            this.questionnaire.questions.push(question);
          }
        });
        return of([]);
      },
      results: {
        list: () => {
          return of(this.analyses);
        },
        get: (id: string) => {
          return of(this.analyses[id]?.results[id]);
        },

        save: (id: string, name: string, result: NewResultModel) => {
          if (!this.analyses[id]) {
            this.analyses[id] = {
              id,
              name,
              results: {},
            };
            this.analyses[id].results[id] = result;
            return of(result);
          } else {
            this.analyses[id].results[id] = result;
            return of(result);
          }
        },
        delete: (id: string = null) => {
          if (id) {
            delete this.analyses[id];
          } else {
            this.analyses = {};
          }
          return of();
        },
      },
    };
  }

  private getEmptyQuestionnaire() {
    return {
      inventoryItemId: null,
      questions: [],
    };
  }
}

interface Results {
  list: (
    id: string,
    type: AnalysesTypesLowercase,
    days?: number,
    token?: string,
    count?: number,
    page?: number
  ) => Observable<{ results: HistoryResult[]; token?: any }>;
  save: (
    id: string,
    result: NewResultModel,
    type: AnalysesTypesLowercase,
    editMode: boolean,
    singleBlood?: boolean
  ) => Observable<any>;
  get(
    id: string,
    resultId: string,
    type: AnalysesTypesLowercase
  ): Observable<any>;
  delete(id: string, resultId?: string, type?: AnalysesTypes): Observable<any>;
}
interface Local {
  results: {
    list: () => Observable<any>;
    save: (id: string, name: string, result: NewResultModel) => any;
    get(id: string): Observable<any>;
    delete(
      id: string,
      resultId?: string,
      type?: AnalysesTypes
    ): Observable<any>;
  };
  getQuestionnaire(): any;
  saveQuestionnaire(questionnaire: NewHabitsResult): any;
  clearQuestionnaire(): void;
}

interface Groups {
  list(): Observable<BloodPanelGroup[]>;
  get(id: string): Observable<BloodPanelGroupAnalyses>;
}

interface BloodPanelGroup {
  id: string;
  image: string;
  name: string;
  analyses$?: Observable<BloodPanelGroupAnalysis[]>;
}

interface BloodPanelGroupAnalysis {
  canAdd: boolean;
  canEdit: boolean;
  id: string;
  isVisibleResult: boolean;
  name: string;
  result: string;
  shortName: string;
  synonyms: any[];
  type: string;
}

interface BloodPanelGroupAnalyses {
  analyses: BloodPanelGroupAnalysis[];
  id: string;
  name: string;
}
